<script lang="ts">import Text from '../Typography/Text/Text.svelte';
import { isAtLeastSmallScreen, isTouchDevice } from '../../helpers/browser';
import { transitionDuration } from '../../helpers/duration';
import Info from './TooltipIcon/Info.svelte';
const { headingText = '', headingLevel = 'h4', ariaLabel = 'More information', bodyText, class: className = '', ...rest } = $props();
let tooltipButton;
let tooltipPopover;
let innerHeight = $state(0);
let innerWidth = $state(0);
let scrollY = $state(0);
// Popover requires an ID, so making sure the ID is unique without doing Math.random()
// https://svelte.dev/docs/svelte/$props#$props.id()
const tooltipId = $props.id();
// Variable to leave some gap between the button and the tooltip
const TOOLTIP_OFFSET = 10;
// Variable for the class that is added/removed for the closing animation
const TOOLTIP_CLOSING_CLASS = 'Tooltip__popover--is-closing';
const tooltipPopoverPosition = $state({
    top: '0px',
    left: '0px',
});
const delays = new Map([
    ['show', 200],
    ['hide', transitionDuration('default')],
]);
const timeouts = new Map();
/**
 * Function to manage a delayed action for the tooltip to prevent accidental triggering. This prevents the tooltip
 * from appearing or disappearing too quickly whenever someone is moving around the page with their cursor
 *
 * @param type - The type of action (show or hide)
 * @param action - The action to execute after the delay (.showPopover() or .hidePopover())
 */
function manageTimeout(type, action) {
    // Clear existing timeout for the type to prevent duplicate timers
    clearTimeout(timeouts.get(type));
    // Set a new timeout for the type
    timeouts.set(type, setTimeout(() => {
        if (type === 'hide') {
            // If this is the hide action, then check if the button or tooltip are still being hovered because the
            // tooltip should not be hidden yet
            if (document.activeElement === tooltipButton ||
                document.activeElement === tooltipPopover ||
                tooltipButton?.matches(':hover') ||
                tooltipPopover?.matches(':hover')) {
                return;
            }
        }
        // Execute the action
        action();
    }, 
    // Use the delay time from the delays map
    delays.get(type) ?? 0));
}
function positionTooltip() {
    if (!tooltipButton || !tooltipPopover) {
        return;
    }
    // Similar to the details HTML element where we need the element to not be display: none; to have any measurements
    tooltipPopover.style.display = 'block';
    const tooltipButtonRect = tooltipButton.getBoundingClientRect();
    const tooltipPopoverHeight = tooltipPopover.offsetHeight;
    const tooltipPopoverWidth = tooltipPopover.offsetWidth;
    tooltipPopover.style.display = '';
    if (isAtLeastSmallScreen()) {
        let tooltipX;
        let tooltipY = scrollY + tooltipButtonRect.top;
        // Default the tooltip position to the top right of the button
        const rightPosition = tooltipButtonRect.right + TOOLTIP_OFFSET;
        // If the button is too close to the right of the window resulting in the tooltip overflowing, then move it where
        // the tooltip is on the left side of the button
        const leftPosition = tooltipButtonRect.left - tooltipPopoverWidth - TOOLTIP_OFFSET;
        if (rightPosition + tooltipPopoverWidth <= innerWidth) {
            // If the tooltip fits on the right, then place it on the right
            tooltipX = rightPosition;
        }
        else if (leftPosition >= 0) {
            // If the tooltip doesn't fit on the right but fits on the left, then place it on the left
            tooltipX = leftPosition;
        }
        else {
            // The tooltip should be above the button and centered on the X-axis when it doesn't fit on the right or left
            // side
            tooltipX = Math.max(TOOLTIP_OFFSET, Math.min(innerWidth - tooltipPopoverWidth - TOOLTIP_OFFSET, tooltipButtonRect.left + (tooltipButtonRect.width - tooltipPopoverWidth) / 2));
            // If the button is in the top 25% of the window, then place the popover below it
            if (tooltipButtonRect.top < innerHeight / 4) {
                tooltipY = scrollY + tooltipButtonRect.bottom + TOOLTIP_OFFSET;
            }
            else {
                tooltipY = scrollY + tooltipButtonRect.top - tooltipPopoverHeight - TOOLTIP_OFFSET;
            }
        }
        tooltipPopoverPosition.top = `${tooltipY}px`;
        tooltipPopoverPosition.left = `${tooltipX}px`;
    }
    else {
        // The tooltip should be above the button and centered on the X-axis when the screen size is small
        tooltipPopoverPosition.left = `${innerWidth / 2 - tooltipPopoverWidth / 2}px`;
        let tooltipY = scrollY + tooltipButtonRect.top - tooltipPopoverHeight - TOOLTIP_OFFSET;
        // If the button is in the top 25% of the window, then place the popover below it
        if (tooltipButtonRect.top < innerHeight / 4) {
            tooltipY = scrollY + tooltipButtonRect.bottom + TOOLTIP_OFFSET;
        }
        tooltipPopoverPosition.top = `${tooltipY}px`;
    }
}
function showTooltip() {
    positionTooltip();
    manageTimeout('show', () => {
        tooltipPopover.classList.remove(TOOLTIP_CLOSING_CLASS);
        tooltipPopover.showPopover();
    });
}
function hideTooltip() {
    manageTimeout('hide', () => {
        // This class is needed because :popover-open is still true, so there needs to be a class to animate out before
        // the tooltip goes to display: none;
        tooltipPopover.classList.add(TOOLTIP_CLOSING_CLASS);
        setTimeout(() => {
            tooltipPopover.classList.remove(TOOLTIP_CLOSING_CLASS);
            tooltipPopover.hidePopover();
        }, transitionDuration('quick'));
    });
}
function focusoutTooltip(event) {
    if (!tooltipButton.contains(event.relatedTarget) && !tooltipPopover.contains(event.relatedTarget)) {
        hideTooltip();
    }
}
function toggleTooltip(event) {
    // If the user is on a touch device, then use the default behavior of the popover attribute and return early
    if (isTouchDevice()) {
        return;
    }
    event.preventDefault();
    if (tooltipPopover.matches(':popover-open')) {
        hideTooltip();
    }
    else {
        showTooltip();
    }
}
</script>

<svelte:window bind:innerHeight bind:innerWidth bind:scrollY onresize={positionTooltip} />

<div
  {...rest}
  class="Tooltip {className}"
  style:--tooltip-popover-position-top={tooltipPopoverPosition.top}
  style:--tooltip-popover-position-left={tooltipPopoverPosition.left}
  data-color-mode="dark"
>
  <button
    class="Tooltip__button"
    popovertarget={tooltipId}
    aria-label={ariaLabel}
    bind:this={tooltipButton}
    onclick={toggleTooltip}
    onmouseenter={showTooltip}
    onmouseleave={hideTooltip}
    onfocusout={focusoutTooltip}
  >
    <Info />
  </button>
  <div
    id={tooltipId}
    class="Tooltip__popover"
    role="tooltip"
    aria-describedby={tooltipId}
    popover="auto"
    bind:this={tooltipPopover}
    onmouseenter={() => clearTimeout(timeouts.get('hide'))}
    onmouseleave={hideTooltip}
    onfocusout={focusoutTooltip}
  >
    {#if headingText}
      <Text class="Tooltip__heading" as={headingLevel} variant="100" value={headingText} />
    {/if}
    {@render bodyText?.()}
  </div>
</div>

<style>@layer design-system-components.pwds {
  .Tooltip {
    position: relative;
  }
  .Tooltip :global(svg) {
    fill: var(--color-neutral-500);
    transition: fill var(--duration-quick) var(--easing-decelerate);
  }
  .Tooltip :global(.Tooltip__heading) {
    margin-bottom: var(--space-10);
  }
  .Tooltip__button {
    display: flex;
  }
  .Tooltip__button:focus-visible:not(:active) {
    outline: var(--accessibility-focus-outline-width) var(--accessibility-focus-outline-style, solid) var(--accessibility-focus-outline-color);
    border-radius: 100%;
    outline-offset: var(--accessibility-focus-outline-offset);
  }
  .Tooltip__button:hover :global(svg) {
    fill: var(--color-neutral-200);
  }
  .Tooltip__popover {
    --color: var(--color-white);
    position: absolute;
    top: var(--tooltip-popover-position-top);
    left: var(--tooltip-popover-position-left);
    width: 90vw;
    max-width: var(--tooltip-popover-max-width);
    padding: var(--space-20);
    background-color: var(--color-black);
    border-radius: var(--tooltip-popover-border-radius);
    box-shadow: var(--tooltip-popover-box-shadow-primary), var(--tooltip-popover-box-shadow-ambient);
    visibility: hidden;
    opacity: 0;
  }
  .Tooltip__popover:popover-open {
    visibility: visible;
    opacity: 1;
    animation: fadeIn var(--duration-quick) var(--easing-decelerate) forwards;
  }
  .Tooltip__popover:popover-open.Tooltip__popover--is-closing {
    animation: fadeOut var(--duration-quick) var(--easing-decelerate) forwards;
  }
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  @keyframes fadeOut {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }
}</style>
